/**
 * Nível EFETIVO de um registro TARP (GUPM-207) — TS puro.
 *
 * O nível base vem do catálogo (`TARP_CATALOG[code].nivel`). Mas um fluxo-gatilho
 * pode ESCALAR para emergência: se o caminho percorrido (`flow_state.path`, já
 * persistido) atinge um terminal `paebm`, o registro é EMERGÊNCIA — mesmo que o
 * nível base seja ALERTA (ex.: PS-TILT) ou ATENÇÃO. Derivado do caminho, sem
 * migration. Alimenta lista/cores/prazo da baixa. **Não** alimenta mais o
 * semáforo da estrutura: o GUPM-291 removeu a promoção automática — este é o
 * nível da TARP, não o da barragem.
 *
 * Distinto da declaração ANM formal (NE-1/2/3), que é ato do RT na baixa
 * (GUPM-176/182) — aqui é o nível OPERACIONAL do registro.
 */
import type { TarpLevel } from '../types';
import { outcome, type FlowState } from './flow';
import type { TarpRecordStatus } from './status';
import { tarpEntry } from './catalog';

/** Nível base do catálogo (sem escalada de fluxo). NORMAL se o código não existir. */
export function nivelBase(tarpCode: string): TarpLevel {
  return tarpEntry(tarpCode)?.nivel ?? 'NORMAL';
}

/**
 * Nível efetivo do registro: EMERGENCIA se o caminho atinge um terminal `paebm`;
 * senão o nível base do catálogo. `flow_state` ausente/vazio ou código/fluxo
 * desconhecido → nível base (ou NORMAL se o código não existir).
 */
export function nivelEfetivo(tarpCode: string, flowState: FlowState | null): TarpLevel {
  const entry = tarpEntry(tarpCode);
  const base = entry?.nivel ?? 'NORMAL';
  const flow = entry?.flow;
  if (!flow || !flowState || flowState.path.length === 0) return base;
  const lastId = flowState.path[flowState.path.length - 1];
  const node = lastId ? flow.nodes[lastId] : undefined;
  return node && outcome(node) === 'paebm' ? 'EMERGENCIA' : base;
}

/**
 * Nível efetivo EXIBÍVEL de um registro (GUPM-207, fix self-review #118).
 * Igual ao `nivelEfetivo`, EXCETO para um rascunho `EM_ABERTURA`: este NÃO escala
 * para EMERGÊNCIA ainda que o `flow_state.path` já atinja o `paebm`. O autosave
 * grava o caminho a cada navegação — antes do clique em "Registrar" —, então um
 * rascunho pode "tocar" o terminal sem ter sido acionado; escalá-lo mostraria uma
 * emergência falsa (🚨 vermelho) na lista de abertos, para todos, conflitando com
 * a própria tarja "Em abertura". Mantém paridade com `contarTarpsPorBarragem`
 * (aggregate.ts), que exclui EM_ABERTURA do status da estrutura pela mesma razão.
 */
export function nivelEfetivoVisivel(record: {
  tarpCode: string;
  flowState: FlowState | null;
  status: TarpRecordStatus;
}): TarpLevel {
  if (record.status === 'EM_ABERTURA') return nivelBase(record.tarpCode);
  return nivelEfetivo(record.tarpCode, record.flowState);
}
